import { Fragment, FC, useEffect, useState } from "react";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import Close from "@mui/icons-material/Close";
import { downloadFile } from "./utils";

import * as XLSX from "xlsx";
// for csv upload
import Papa from "papaparse";

import { LoadingTextAnimation } from "components/common/loader";
import { getParsedDate } from "components/utils/utilFunctions/function";
import { isValid } from "date-fns";
import { useTranslation } from "react-i18next";
import { GridMetaDataType } from "components/dataTableStatic";
import { GridWrapperStatic as GridWrapper } from "components/dataTableStatic/gridWrapper";
import { GradientButton } from "components/styledComponent/button";

const transformedColumn = (fileData: any[]) => {
  const columns = fileData[0]?.map((col, index) => ({
    accessor: col,
    columnName: col,
    sequence: index + 1,
    componentType: "default",
    width: 180,
    minWidth: 120,
    maxWidth: 600,
    showTooltip: true,
  }));

  let finalColumn: any = [];
  if (columns) {
    finalColumn = [
      {
        columnName: "UNIQUE",
        accessor: "UNIQUE",
        componentType: "default",
        isVisible: false,
      },
      ...columns,
    ];
  }

  return finalColumn;
};

const transformedGridData = (fileData: any[]) => {
  const gridData = fileData.slice(1).map((row) => {
    return row.reduce((acc, curr, i) => {
      return { ...acc, [fileData[0][i]]: curr };
    }, {});
  });
  let finalGridData: any = [];
  if (gridData) {
    finalGridData = gridData.map((row, index) => ({
      UNIQUE: index + 1,
      ...row,
    }));
  }

  return finalGridData;
};

const generateMetada = (fileData: any[]) => {
  const metaData: GridMetaDataType = {
    gridConfig: {
      gridLabel: "",
      rowIdColumn: "UNIQUE",
      defaultColumnConfig: {
        width: 400,
        maxWidth: 450,
        minWidth: 300,
      },
      hideHeader: true,
      disableGroupBy: true,
      enablePagination: true,
      pageSizes: [15, 30, 50],
      defaultPageSize: 15,
      containerHeight: {
        min: "67vh",
        max: "70vh",
      },
      allowFilter: false,
      allowColumnHiding: false,
      allowRowSelection: false,
    },
    columns: transformedColumn(fileData) ?? [],
  };

  return metaData;
};

export const ExcelViewer: FC<{
  blob: File;
  fileName: string;
  onClose?: any;
}> = ({ blob, fileName, onClose }) => {
  const [excelData, setExcelData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleFileUpload = () => {
    const reader = new FileReader();

    setIsLoading(true);
    reader.onload = (event: any) => {
      const data = event.target.result;
      const workbook = XLSX.read(data, {
        type: "binary",
        raw: false,
      });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const jsonSheet: any = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
      });
      const parsedSheet = jsonSheet.map((row) => {
        return row.map((cell) => {
          const dateCell = getParsedDate(cell, true);
          if (dateCell instanceof Date && isValid(dateCell)) {
            return dateCell.toLocaleDateString("en-UK");
          }
          return cell;
        });
      });
      setIsLoading(false);
      setExcelData(parsedSheet);
    };
    if (blob) reader.readAsBinaryString(blob);
    else setExcelData([]);
  };

  useEffect(() => {
    handleFileUpload();
  }, []);

  return (
    <Fragment>
      <DialogActions style={{ display: "flex", padding: "8px 24px" }}>
        <Typography variant="h6" color="textSecondary">
          File:
        </Typography>
        <Typography variant="h6">{fileName}</Typography>
        <div style={{ flexGrow: 1 }}></div>
        {/* <IconButton
          color="secondary"
          onClick={() => downloadFile(blob, fileName)}
        >
          <GetAppRoundedIcon />
        </IconButton> */}
        {typeof onClose === "function" ? (
          <IconButton color="secondary" onClick={onClose}>
            <Close />
          </IconButton>
        ) : null}
      </DialogActions>
      <DialogContent>
        {isLoading ? <LoadingTextAnimation /> : null}
        {excelData.length > 0 ? (
          <GridWrapper
            key={"previewExcelFileData"}
            finalMetaData={generateMetada(excelData ?? [])}
            data={transformedGridData(excelData) ?? []}
            setData={() => null}
          />
        ) : (
          <p style={{ textAlign: "center", color: "#888" }}>
            <em>{isLoading ? "File is Loading..." : "Empty file found!!!"}</em>
          </p>
        )}
      </DialogContent>
    </Fragment>
  );
};

//csv
export const CsvViewer: FC<{
  blob: File;
  fileName: string;
  onClose?: any;
}> = ({ blob, fileName, onClose }) => {
  const [csvData, setCsvData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleFileUpload = async () => {
    setIsLoading(true);
    Papa.parse(blob, {
      header: false,
      skipEmptyLines: true,
      complete: (result) => {
        setIsLoading(false);
        const parsedSheet = result.data.map((row) => {
          return row.map((cell) => {
            const dateCell = getParsedDate(cell, true);
            if (dateCell instanceof Date && isValid(dateCell)) {
              return dateCell.toLocaleDateString("en-UK");
            }
            return cell;
          });
        });
        setCsvData(parsedSheet);
      },
      error: (error) => {
        setIsLoading(false);
        setCsvData([]);
      },
    });
  };

  useEffect(() => {
    handleFileUpload();
  }, []);

  return (
    <Fragment>
      <DialogActions style={{ display: "flex", padding: "8px 24px" }}>
        <Typography variant="h6" color="textSecondary">
          File:
        </Typography>
        <Typography variant="h6">{fileName}</Typography>
        <div style={{ flexGrow: 1 }}></div>
        {typeof onClose === "function" ? (
          <IconButton color="secondary" onClick={onClose}>
            <Close />
          </IconButton>
        ) : null}
      </DialogActions>
      <DialogContent>
        {isLoading ? <LoadingTextAnimation /> : null}
        {csvData.length > 0 ? (
          <GridWrapper
            key={"previewCsvFileData"}
            finalMetaData={generateMetada(csvData ?? [])}
            data={transformedGridData(csvData) ?? []}
            setData={() => null}
          />
        ) : (
          <p style={{ textAlign: "center", color: "#888" }}>
            <em>{isLoading ? "File is Loading..." : "Empty file found!!!"}</em>
          </p>
        )}
      </DialogContent>
    </Fragment>
  );
};

export const PDFViewer: FC<{
  blob: File;
  fileName: string;
  onClose?: any;
  optionalActionButton?: {
    callback: () => void;
    label: string;
  };
}> = ({ blob, fileName, onClose, optionalActionButton }) => {
  const { t } = useTranslation();
  const [urlObj, setUrlObj] = useState(
    typeof blob === "object" && Boolean(blob)
      ? URL.createObjectURL(blob as any)
      : null,
  );
  //console.log(blob, urlObj);
  useEffect(() => {
    let toRemoveURL = urlObj ?? "";
    return () => {
      URL.revokeObjectURL(toRemoveURL);
    };
  }, []);
  useEffect(() => {
    setUrlObj(
      typeof blob === "object" && Boolean(blob)
        ? URL.createObjectURL(blob as any)
        : null,
    );
  }, [blob]);

  return (
    <Fragment>
      <DialogActions style={{ display: "flex", padding: "8px 24px" }}>
        <Typography variant="h6" color="textSecondary">
          {t("File")}:
        </Typography>
        <Typography variant="h6">{fileName}</Typography>
        <div style={{ flexGrow: 1 }}></div>
        {Boolean(optionalActionButton) ? (
          <GradientButton
            color="primary"
            onClick={() => {
              if (typeof optionalActionButton?.callback === "function")
                optionalActionButton.callback();
            }}
          >
            {optionalActionButton?.label ?? "NO_LABEL"}
          </GradientButton>
        ) : null}
        <IconButton
          color="secondary"
          onClick={() => downloadFile(blob, fileName)}
        >
          <GetAppRoundedIcon />
        </IconButton>
        {typeof onClose === "function" ? (
          <IconButton color="secondary" onClick={onClose}>
            <Close />
          </IconButton>
        ) : null}
      </DialogActions>
      <DialogContent>
        <iframe
          title="Document Preview"
          src={`${urlObj}`}
          style={{ height: "100%", width: "100%" }}
          aria-label="PDF Preview"
        />
      </DialogContent>
    </Fragment>
  );
};

export const ImageViewer: FC<{
  blob: File;
  fileName: string;
  onClose?: any;
}> = ({ blob, fileName, onClose }) => {
  const [urlObj, setUrlObj] = useState(
    typeof blob === "object" && Boolean(blob)
      ? URL.createObjectURL(blob as any)
      : "",
  );

  useEffect(() => {
    let toRemoveURL = urlObj ?? "";

    return () => URL.revokeObjectURL(toRemoveURL);
  }, []);
  useEffect(() => {
    setUrlObj(
      typeof blob === "object" && Boolean(blob)
        ? URL.createObjectURL(blob as any)
        : "",
    );
  }, [blob]);
  return (
    <Fragment>
      <DialogActions style={{ display: "flex", padding: "8px 24px" }}>
        <Typography variant="h6" color="textSecondary">
          File:
        </Typography>
        <Typography variant="h6">{fileName}</Typography>
        <div style={{ flexGrow: 1 }}></div>
        <IconButton
          color="secondary"
          onClick={() => downloadFile(blob, fileName)}
        >
          <GetAppRoundedIcon />
        </IconButton>
        {typeof onClose === "function" ? (
          <IconButton color="secondary" onClick={onClose}>
            <Close />
          </IconButton>
        ) : null}
      </DialogActions>
      <DialogContent>
        <img width="60%" src={`${urlObj}`} alt="Preview of document" />
      </DialogContent>
    </Fragment>
  );
};

export const NoPreview: FC<{
  fileName: string;
  onClose?: any;
  message?: string;
}> = ({ onClose, fileName, message }) => (
  <Fragment>
    <DialogActions style={{ display: "flex", padding: "8px 24px" }}>
      <Typography variant="h6" color="textSecondary">
        File:
      </Typography>
      <Typography variant="h6">{fileName}</Typography>
      <div style={{ flexGrow: 1 }}></div>
      {typeof onClose === "function" ? (
        <IconButton color="secondary" onClick={onClose}>
          <Close />
        </IconButton>
      ) : null}
    </DialogActions>
    <DialogContent>{message}</DialogContent>
  </Fragment>
);
